import { useMutation } from "@apollo/client/react";
import { Grid } from "@mui/material";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import { calcularDiferenciaHoras } from "../../../../../../utils/helpers";
import CardBox from "../../CardBox/CardBox";

import SAVE_CONCLUSION_PRESENCIAL, { adaptAddVisitaConclusionPresencialInput } from "../../../../../../data/graphQL/mutations/Visitas/saveConclusionPresencial";

import toast from "react-hot-toast";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import { idTiposProfesionales } from "../../../../constants/tiposProfesionales";
import { useDeleteArchivoVisita } from "../../../../hooks/useDeleteArchivoVisita";
import { useStepSave } from "../../../../hooks/useStepSave";
import { handleChangeImageVisita } from "../../../../utils/handleChangeImageVisita";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import MultipleAdjuntos from "../../MultipleAdjuntos";

const TIPO_EVALUACION_PP = 2;

export default function ConclusionPresencial({
  onSaveRef,
  isClone,
  idEvaluacion,
  onExposeFormatted,
  readOnly = false,
  esEnfermeroGuardia
}) {
  const { visita, setVisita, saveArchivoVisita } = useVisita();
  const { deleteArchivo } = useDeleteArchivoVisita();
  const [formData, setFormData] = useState({ capacitacion: false });

  const [save] = useMutation(SAVE_CONCLUSION_PRESENCIAL);

  const idUsuario = parseInt(sessionStorage.getItem("idUsuario"), 10);

  useEffect(() => {
    if (visita?.idUsuarioCreador !== idUsuario && !readOnly) {
      handleChange('conformidad', false);
    }
  }, [idUsuario, visita?.idUsuarioCreador]);

  const formattedInputs = useMemo(() => {
    return conclusionPresencialInputs.map((input) => {
      const newInput = { ...input };

      switch (input.name) {
        case "adjuntoCapacitacion":
          newInput.required = !!formData.capacitacion;
          newInput.disabled = !formData.capacitacion;
          break;
        case "horasTotales":
        case "horasComputar":
          newInput.hidden = !esEnfermeroGuardia;
          break;
        case "justificacion":
          newInput.hidden = !esEnfermeroGuardia;
          newInput.required = (esEnfermeroGuardia) && (formData?.horasTotales) && (parseInt(formData.horasTotales.substring(0, 2), 10) >= parseInt(formData.horasComputar, 10));
          break;
        case 'conformidad':
          newInput.disabled = !!visita?.idUsuarioCreador && visita?.idUsuarioCreador != idUsuario;
          break;
        case "obsConformidad":
          newInput.required = !formData.conformidad;
          break;
        default:
          break;
      }

      if (["fechaFin", "capacitacion", "adjuntoCapacitacion"].includes(newInput.name) && !!isClone) newInput.disabled = true;

      if (readOnly) newInput.disabled = true;

      return newInput;
    });
  }, [
    formData.capacitacion,
    formData.conformidad,
    formData.horasTotales,
    formData.horasComputar,
    formData.fechaFin,
    visita.fechaVisita,
    readOnly,
    isClone,
    visita,
    esEnfermeroGuardia]
  );

  const handleChange = useCallback((name, value) => {
    const extra = (name === 'capacitacion' && !value)
      ? { adjuntoCapacitacion: null }
      : {};

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...extra,
    }));
  }, []);

  const onChangeImage = (name, fileValue) => {
    handleChangeImageVisita({
      saveArchivoVisita,
      name,
      fileValue,
      idVisita: visita.idVisita,
      handleChange,
      idTipoArchivo: tipoArchivoVisita.CAPACITACION
    });
  };

  const onDeleteImage = (name) => {
    const file = formData[name];
    deleteArchivo({
      idArchivo: file.idArchivo,
      name: name,
      handleChange
    });
  };

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedInputs, formData });

    const fechaInicio = moment(visita.fechaVisita);
    const fechaFin = moment(formData.fechaFin);

    if (!fechaFin.isValid()) {
      toast.error("La fecha de finalización es inválida.");
      const error = new Error("Validación fallida");
      error.isCustomError = true;
      throw error;
    }

    if (fechaFin.isSameOrBefore(fechaInicio, 'minute')) {
      toast.error("La fecha de finalización debe ser posterior a la fecha de inicio de la visita.");
      const error = new Error("Validación fallida");
      error.isCustomError = true;
      throw error;
    }

    if (fechaFin.isAfter(moment())) {
      toast.error("La fecha de finalización no puede ser mayor a la fecha y hora actual");
      const error = new Error("Validación fallida");
      error.isCustomError = true;
      throw error;
    }

    const { data } = await save({ variables: { input: adaptAddVisitaConclusionPresencialInput(formData) } });

    const idVisitaResponse = data?.saveVisitaConclusionPresencial?.idVisita;
    if (!idVisitaResponse) throw new Error("Error al guardar");

    setVisita((prev) => ({
      ...prev,
      ...formData
    }));

  });

  useEffect(() => {
    setFormData((prevDatos) => ({
      ...prevDatos,
      ...visita,
      conformidad: visita?.conformidad === false ? false : true,
      capacitacion: !!visita?.capacitacion
    })
    );
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: 'conclusionPresencial',
      stepName: 'Conclusión',
      formattedInputs
    });
  }, [onExposeFormatted, formattedInputs]);

  useEffect(() => {
    if (visita.idTipoProfesional == idTiposProfesionales.ENFERMERO_GUARDIA) {
      if (visita.fechaVisita && formData.fechaFin) {
        const horasTotales = calcularDiferenciaHoras(
          moment(visita.fechaVisita),
          moment(formData.fechaFin)
        );
        handleChange('horasTotales', horasTotales + " hs");
      }
      if (visita?.profesionales) {
        const asignacion = visita.profesionales.filter(x => x.idTipoProfesional == visita.idTipoProfesional && x.idProfesional == visita.idProfesional);
        if (asignacion != null && asignacion.length > 0) {
          handleChange('horasComputar', asignacion[0].cantDiasHoras + " hs");
        }
      }
    }
  }, [visita, visita.fechaVisita, formData.fechaFin]);

  return (
    <CardBox>
      <Grid container spacing={2}>
        {formattedInputs.
          filter((input) => !input.hidden).
          map((input) =>
            <Grid size={{ xs: 12 }} key={input.name}>
              {input.name === 'conclusionPresencialAdjuntos' &&
                <MultipleAdjuntos
                  input={input}
                  archivos={formData[input.name]}
                  handleChange={handleChange}
                  idVisita={visita.idVisita} />

              }
              {input.name !== 'conclusionPresencialAdjuntos' && <RenderInputByTypeMobile
                value={formData[input.name]}
                label={input.displayName}
                handleChange={input.name === 'adjuntoCapacitacion' ? onChangeImage : handleChange}
                handleDelete={input.name === 'adjuntoCapacitacion' ? onDeleteImage : null}
                input={input} />
              }
            </Grid>
          )}
      </Grid>
    </CardBox>);

}

const conclusionPresencialInputs = [
  {
    displayName: "Evolución",
    name: "evolucion",
    type: "long_string",
    required: true,
    maxLength: 4000
  },
  {
    displayName: "Subir imagen",
    name: "conclusionPresencialAdjuntos",
    type: "file"
  },
  {
    displayName: "Capacitación",
    name: "capacitacion",
    type: "boolean"
  },
  {
    displayName: "Adjuntos Capacitación",
    name: "adjuntoCapacitacion",
    type: "file",
    required: false
  },
  {
    displayName: "Fecha de finalización",
    name: "fechaFin",
    type: "datetime",
    required: true,
    disableFuture: true
  },
  {
    displayName: "Horas Totales",
    name: "horasTotales",
    type: "infoRow"
  },
  {
    displayName: "Horas para computar",
    name: "horasComputar",
    type: "infoRow"
  },
  {
    displayName: "Justificación más horas",
    name: "justificacion",
    type: "long_string",
    required: false
  },
  {
    displayName: "Conformidad",
    name: "conformidad",
    type: "boolean"
  },
  {
    displayName: "Obs. Conformidad",
    name: "obsConformidad",
    type: "long_string",
    required: false,
    maxLength: 4000
  }];